// confluence api: https://confluence.agile.corp.edp.pt/pages/viewpage.action?pageId=274021132
import { ContractSegmentEsEnum } from "@edp-enums/ES/contract-segment-es-enum";
import { ContractStatusEsEnum } from "@edp-enums/ES/contract-status-es-enum";
import { ContractSubtypeEsEnum } from "@edp-enums/ES/contract-subtype-es-enum";
import { ContractTypeEsEnum } from "@edp-enums/ES/contract-type-es-enum";
import moment from "moment";

export class ContractEs {
    constructor(
        public accountId: string,
        public collectiveId: string,
        public contractedPower: number,
        public cups: string,
        public duplicated,
        public electricityPower: number,
        public electronicBilling,
        public grantRepresentative,
        public hasMaintenance,
        public id: string,
        public opportunityId: string,
        public paymentMethod: string,
        public pvp: number,
        public segment: ContractSegmentEsEnum,
        public status: ContractStatusEsEnum,
        public subtype: ContractSubtypeEsEnum,
        public type: ContractTypeEsEnum,
        public supplyCity: string,
        public supplyPostalCode: string,
        public supplyProvince: string,
        public supplyStreet: string,
        public supplyFloor?: string,
        public supplyNumber?: string,
        public supplyMunicipality?: string,
        public supplyDoor?: string,
        public startDate?: string,
        public endDate?: string,
        public signedDate?: string,
        public cancellationDate?: string,
        public desistedReason?: string,
        public desistedSubreason?: string,
        public finalizedReason?: string,
        public desistedComments?: string,
        public channelName?: string,
        public sepaCode?: string,
        public iban?: string,
        public verificationStatus?: string,
        public cancellationReasonDetail?: string,
        public maintenanceEndDate?: string,
        public maintenanceDuration?: number,
        public grantConvened?: string,
        public applicableResolution?: string,
        public contractIcon?: string,
        public contractTypeLabel?: string,
        public contractLabel?: string,
        public accessFee?: string,
        public priceMode?: string,
        public power1?: number,
        public power2?: number,
        public power3?: number,
        public power4?: number,
        public power5?: number,
        public power6?: number,
        public powerList?: any[]
    ) {
        this.mountContractTypeCard(this.type);
    }

    public isActived() {
        return this.status === ContractStatusEsEnum.ACTIVATED || this.status === ContractStatusEsEnum.VALIDATED;
    }

    public searchableFields() {
        return `${this.supplyCity}${this.supplyPostalCode}${this.supplyProvince}${this.supplyStreet}`.toLowerCase();
    }

    public getAddress(): string {
        return `${this.supplyStreet} ${[this.supplyNumber, this.supplyDoor].filter(Boolean).join(", ")} ${this.supplyPostalCode}`;
    }

    public mountContractTypeCard(contractType: ContractTypeEsEnum) {
        switch (contractType) {
            case ContractTypeEsEnum.SOLAR:
                this.contractIcon = "icon-edponline-Bop-007";
                this.contractTypeLabel = "pt.edp.edponline.contract.local.details.label.solar.energy";
                this.contractLabel = this.mountSolarContractLabel();
                break;
            case ContractTypeEsEnum.LIGHT_AND_GAS:
                this.contractIcon = "icon-edponline-Bop-001";
                this.contractTypeLabel = "edp.edponline.contract.local.section.details.contract.type.label";
                this.contractLabel = "edp.edponline.contract.local.section.details.contract.label";
                this.startDate = moment(this.startDate).format("DD/MM/YYYY");
                this.endDate = this.endDate
                    ? moment(this.endDate).format("DD/MM/YYYY")
                    : "edp.edponline.contract.local.section.details.contract.current.contract";
                this.priceMode = this.mountPriceModeLabel();
                this.powerList = this.transformPowerData(this);
                break;
            default:
                this.contractIcon = "icon-edponline-Bop-001";
                break;
        }
    }

    public mountSolarContractLabel(): string {
        switch (true) {
            case this.status !== ContractStatusEsEnum.CANCELLED:
                return "pt.edp.edponline.es.contract.local.details.contract.label";
            case this.status === ContractStatusEsEnum.CANCELLED:
                return "pt.edp.edponline.es.contract.local.details.contract.cancel.label";
            default:
                return "-";
        }
    }

    public mountPriceModeLabel(): string {
        switch (this.priceMode) {
            case PriceModeKey.TRANQUILIDAD:
                return PriceModeValue.TRANQUILIDAD;
            case PriceModeKey.TRANQUILIDAD_XL:
                return PriceModeValue.TRANQUILIDAD_XL;
            case PriceModeKey.MOVILIDAD:
                return PriceModeValue.MOVILIDAD;
            case PriceModeKey.MOVILIDAD_XL:
                return PriceModeValue.MOVILIDAD_XL;
            case PriceModeKey.AHORRO_SOLAR:
                return PriceModeValue.AHORRO_SOLAR;
            case PriceModeKey.AHORRO_SOLAR_XL:
                return PriceModeValue.AHORRO_SOLAR_XL;
            case PriceModeKey.AHORRO_SOLAR_UNIFAMILIAR:
                return PriceModeValue.AHORRO_SOLAR_UNIFAMILIAR;
            default:
                return "-";
        }
    }

    public transformPowerData(data: any): any[] {
        return Object.keys(data)
            .filter(key => /^power[1-6]$/.test(key))
            .filter(key => data[key] !== undefined)
            .map(key => {
                const powerLabel = `P${key.replace("power", "")}`;
                const powerValue = `${data[key]} KW`;
                return { value: powerValue, label: powerLabel };
            });
    }
}

export class ContractEsFactory {
    static create(contract: ContractEs) {
        return new ContractEs(
            contract.accountId,
            contract.collectiveId,
            contract.contractedPower,
            contract.cups,
            contract.duplicated,
            contract.electricityPower,
            contract.electronicBilling,
            contract.grantRepresentative,
            contract.hasMaintenance,
            contract.id,
            contract.opportunityId,
            contract.paymentMethod,
            contract.pvp,
            contract.segment,
            contract.status,
            contract.subtype,
            contract.type,
            contract.supplyCity,
            contract.supplyPostalCode,
            contract.supplyProvince,
            contract.supplyStreet,
            contract.supplyFloor,
            contract.supplyNumber,
            contract.supplyMunicipality,
            contract.supplyDoor,
            contract.startDate,
            contract.endDate,
            contract.signedDate,
            contract.cancellationDate,
            contract.desistedReason,
            contract.desistedSubreason,
            contract.finalizedReason,
            contract.desistedComments,
            contract.channelName,
            contract.sepaCode,
            contract.iban,
            contract.verificationStatus,
            contract.cancellationReasonDetail,
            contract.maintenanceEndDate,
            contract.maintenanceDuration,
            contract.grantConvened,
            contract.applicableResolution,
            contract.contractIcon,
            contract.contractTypeLabel,
            contract.contractLabel,
            contract.accessFee,
            contract.priceMode,
            contract.power1,
            contract.power2,
            contract.power3,
            contract.power4,
            contract.power5,
            contract.power6,
            contract.powerList
        );
    }
}

export enum PriceModeKey {
    TRANQUILIDAD = "2TP1TE",
    TRANQUILIDAD_XL = "2TP1TEXL",
    MOVILIDAD = "2TP2TE",
    MOVILIDAD_XL = "2TP2TEXL",
    AHORRO_SOLAR = "2TP3TE",
    AHORRO_SOLAR_XL = "2TP3TEXL",
    AHORRO_SOLAR_UNIFAMILIAR = "6TP6TE",
}

export enum PriceModeValue {
    TRANQUILIDAD = "Tranquilidad (2TP1TE)",
    TRANQUILIDAD_XL = "Tranquilidad (2TP1TEXL)",
    MOVILIDAD = "Movilidad (2TP2TE)",
    MOVILIDAD_XL = "Movilidad (2TP2TEXL)",
    AHORRO_SOLAR = "Ahorro Solar (2TP3TE)",
    AHORRO_SOLAR_XL = "Ahorro Solar (2TP3TEXL)",
    AHORRO_SOLAR_UNIFAMILIAR = "Ahorro Solar Unifamiliar (6TP6TE)",
}
